import {
  Alert,
  Button,
  Checkbox,
  CondensedButton,
  Flex,
  FormFieldGroup,
  Heading,
  IconTrashLine,
  List,
  ScreenReaderContent,
  Spinner,
  Text,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import { CourseService } from "../../../../services/CourseService";
import { sort } from "../../../../utils/utils";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import { MULTI, Selector, SINGLE } from "../../../Selectors/Selector";
import AcademicYearSelect from "../../../TermManagement/AcademicYears/AcademicYearSelect";
import { ReviewCycleContext } from "../Review";

const CycleCreation = ({ editingCycle, setEditingCycle, cycleLengths }) => {
  const showNotification = useNotification();
  const { departments, courseLevels, reflectionTemplates, courseReviewCycles } =
    useContext(ReviewCycleContext);
  const departmentOptions = departments.map((dept) => ({
    id: dept.id,
    label: dept.name,
  }));
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCourseLevels, setSelectedCourseLevels] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [submitFindLoad, setSubmitFindLoad] = useState(false);
  const [noCourses, setNoCourses] = useState(false);

  function handleSelectCycleLength(selectedLength) {
    setEditingCycle((prevCycle) => ({
      ...prevCycle,
      duration: cycleLengths.find((length) => length.id === selectedLength),
    }));
  }

  function handleSelectYear(selectedYear) {
    setEditingCycle((prevCycle) => ({
      ...prevCycle,
      startYear: selectedYear[0],
    }));
  }

  function handleSelectTemplate(selectedTemplate) {
    setEditingCycle((prevCycle) => ({
      ...prevCycle,
      reflectionTemplateId: selectedTemplate,
    }));
  }

  function handleSelectedDepartments(selectedDepts) {
    if (selectedDepts.length === 0) {
      setSelectedCourseLevels([]);
    }
    setSelectedDepartments(
      departments.filter((dept) => selectedDepts.includes(dept.id))
    );
  }

  function handleSelectedCourseLevels(selectedLevels) {
    setSelectedCourseLevels(selectedLevels);
  }

  async function handleFindCourses() {
    try {
      setSubmitFindLoad(true);
      const courses = await CourseService.getCourses({
        ownerCourseCode: selectedDepartments.map((dep) => dep.title),
        courseLevels: selectedCourseLevels,
        approval: "off",
        withOutcomes: false,
      });
      setNoCourses(courses.data.length === 0);
      setFilteredCourses(sort(courses.data, (course) => course.code));
    } catch {
      showNotification(
        t("notifications.general.fetchError", { obj: t("programs.courses") }),
        ERROR
      );
    } finally {
      setSubmitFindLoad(false);
    }
  }

  function updateSelectedCourses(courseId) {
    const index = editingCycle.courses.findIndex(
      (course) => course.id === courseId
    );
    if (index === -1) {
      const course = filteredCourses.find((c) => c.id === courseId);
      setEditingCycle((prevCycle) => ({
        ...prevCycle,
        courses: [...prevCycle.courses, course],
      }));
    } else {
      setEditingCycle((prevCycle) => ({
        ...prevCycle,
        courses: [
          ...prevCycle.courses.slice(0, index),
          ...prevCycle.courses.slice(index + 1),
        ],
      }));
    }
  }

  function removeCourse(courseToRemove) {
    const newSelectedCourses = editingCycle.courses.filter(
      (course) => course.id !== courseToRemove
    );
    setEditingCycle((prevCycle) => ({
      ...prevCycle,
      courses: newSelectedCourses,
    }));
  }

  function alreadyAssociatedToCycle(course) {
    return courseReviewCycles.some(
      (cycle) =>
        cycle.id !== editingCycle.id &&
        cycle.reviewables.some((c) => c.id === course.id)
    );
  }

  return (
    <>
      <Heading border="bottom" level="h5" as="h2" margin="0 0 small 0">
        {t("courseReviewCycle.cycleDetails")}
      </Heading>
      <Flex alignItems="start" padding="0 0 small 0" gap="small" wrap="wrap">
        <Flex.Item size="50%">
          <Selector
            id="cycle-length-selector"
            options={cycleLengths}
            selectHandler={handleSelectCycleLength}
            label={
              <Text size="small">{t("courseReviewCycle.cycleLength")}</Text>
            }
            selectedItemId={editingCycle.duration.id}
            size="small"
          />
        </Flex.Item>
        <Flex.Item shouldGrow>
          <AcademicYearSelect
            type={SINGLE}
            label={t("reflectionTemplates.startYear")}
            size="small"
            selectHandler={handleSelectYear}
            selectedAcademicYears={editingCycle.startYear.id}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <Selector
            type={SINGLE}
            id="reflection-template-select"
            label={
              <Text size="small">
                {t("reflectionTemplates.reflectionTemplate")}{" "}
                <Text size="small" color="secondary" fontStyle="italic">
                  ({t("form.optional")})
                </Text>
              </Text>
            }
            options={[
              { id: "-1", label: t("global.none") },
              ...reflectionTemplates,
            ]}
            selectHandler={handleSelectTemplate}
            selectedItemId={editingCycle.reflectionTemplateId}
            size="small"
          />
        </Flex.Item>
      </Flex>
      <Heading border="bottom" level="h5" as="h2" margin="0 0 small 0">
        {t("analyze.courseFilterHeading")}
      </Heading>
      <Flex alignItems="end" padding="0 0 small 0" gap="small">
        <Flex.Item size="50%">
          <Selector
            type={MULTI}
            size="small"
            id="ac-sub-selector"
            options={departmentOptions}
            selectHandler={handleSelectedDepartments}
            label={<Text size="small">{t("programs.academicSubjects")}</Text>}
            selectedOptions={selectedDepartments.map((d) => d.id)}
          />
        </Flex.Item>
        <Flex.Item shouldGrow shouldShrink>
          <Selector
            type={MULTI}
            size="small"
            id="course-level-selector"
            options={courseLevels.map((level) => ({
              ...level,
              label: `${t("courseCodePattern.level")} ${level.courseLevel} (${level.range.start} - ${level.range.end})`,
            }))}
            selectHandler={handleSelectedCourseLevels}
            label={<Text size="small">{t("programs.courseLevels")}</Text>}
            interaction={selectedDepartments.length > 0}
            selectedOptions={selectedCourseLevels}
          />
        </Flex.Item>
        <Flex.Item textAlign="end">
          <Button
            size="small"
            color="primary"
            onClick={handleFindCourses}
            interaction={
              selectedDepartments.length === 0 || submitFindLoad
                ? "disabled"
                : "enabled"
            }
          >
            {submitFindLoad ? (
              <Spinner
                renderTitle={t("programs.findingCourses")}
                size="x-small"
              />
            ) : (
              <Text size="small">{t("programs.findCourses")}</Text>
            )}
          </Button>
        </Flex.Item>
      </Flex>
      <Flex alignItems="start" wrap="wrap">
        <Flex.Item size="50%">
          <Heading level="h4" as="h3" margin="0 0 small 0">
            <Text size="small">{t("programs.selectCourses")}:</Text>
          </Heading>
          <Card style={{ height: "350px", overflow: "auto" }}>
            {!noCourses && filteredCourses.length === 0 ? (
              <Alert variant="info">{t("programs.selectDepartments")}</Alert>
            ) : noCourses ? (
              <Alert variant="warning">{t("programs.noCoursesFound")}</Alert>
            ) : (
              <FormFieldGroup
                description={
                  <ScreenReaderContent>
                    <span id="groupLabel">{t("programs.selectCourses")}</span>
                  </ScreenReaderContent>
                }
                rowSpacing="small"
              >
                {filteredCourses.map((course) => (
                  <View as="div" key={course.id}>
                    <Checkbox
                      size="small"
                      label={
                        alreadyAssociatedToCycle(course) ? (
                          <Tooltip
                            renderTip={t("courseReviewCycle.alreadyAssociated")}
                            color="primary-inverse"
                            placement="end"
                          >
                            <span id={`${course.code}Label`}>
                              {course.code}
                            </span>
                          </Tooltip>
                        ) : (
                          <span id={`${course.code}Label`}>{course.code}</span>
                        )
                      }
                      value={course.id}
                      onChange={(event) => {
                        updateSelectedCourses(event.target.value);
                      }}
                      checked={
                        !!editingCycle.courses.find((c) => c.id === course.id)
                      }
                      disabled={alreadyAssociatedToCycle(course)}
                    />
                  </View>
                ))}
              </FormFieldGroup>
            )}
          </Card>
        </Flex.Item>
        <Flex.Item size="50%" padding="0 0 0 medium">
          <Heading level="h4" as="h3" margin="0 0 small 0">
            <Text size="small"> {t("programs.selectedCourses")}</Text>
          </Heading>
          <Card style={{ height: "350px", overflow: "auto" }}>
            <List isUnstyled>
              {editingCycle.courses.map((course, key) => (
                <List.Item key={key} margin="0 0 small 0">
                  <Flex justifyItems="space-between">
                    <Flex.Item>
                      <Text size="small">{course.code}</Text>
                    </Flex.Item>
                    <Flex.Item>
                      <CondensedButton
                        data-testid={`remove-${course.code}`}
                        margin="0 0 xxx-small 0"
                        onClick={() => removeCourse(course.id)}
                      >
                        <ScreenReaderContent>
                          {t("global.remove") + " " + course.code}
                        </ScreenReaderContent>
                        <IconTrashLine />
                      </CondensedButton>
                    </Flex.Item>
                  </Flex>
                </List.Item>
              ))}
            </List>
          </Card>
        </Flex.Item>
      </Flex>
    </>
  );
};

export default CycleCreation;
